import React from 'react';
import ReactMarkdown from 'react-markdown';

import { Box, Button, Typography } from '@mui/material';

import { CommonComponentProps } from '../pages/types';
import EditableMarkdownText from './editable-markdown-text';

export type TextBoxItemType =
  | 'title'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle'
  | 'paragraph'
  | 'body1'
  | 'body2'
  | 'button'
  | 'primaryButton'
  | 'secondaryButton'
  | 'textButton'
  | 'divider';

interface TextBoxProps extends CommonComponentProps {
  texts?: {
    type: TextBoxItemType;
    contents: string;
    link?: string;
  }[];
  textBoxGap?: string;
}

const TextBox: React.FC<TextBoxProps> = ({
  id,
  texts = [],
  textBoxGap = '0',
  sx,
  isEditable,
  onPropsChange,
}) => {
  const handleButtonClick = (link?: string) => {
    if (isEditable) return; // Disable links in edit mode
    if (!link) return;

    // Check if it's an internal scroll link (starts with # and targets an element ID)
    if (link.startsWith('*')) {
      // This is a scroll-to-element link
      const elementId = link.substring(1);
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        console.warn(`Element with ID "${elementId}" not found`);
      }
    } else {
      // External link - open in same window
      window.location.href = link;
    }
  };

  const handleTextChange = (index: number, newContent: string) => {
    if (!onPropsChange) return;
    const newTexts = [...texts];
    newTexts[index] = { ...newTexts[index], contents: newContent };
    onPropsChange({ texts: newTexts });
  };

  const renderEditableText = (
    text: { type: TextBoxItemType; contents: string; link?: string },
    index: number,
    Component: React.ElementType,
    props: any = {}
  ) => {
    return (
      <EditableMarkdownText
        key={index}
        content={text.contents}
        onSave={(newContent) => handleTextChange(index, newContent)}
        isEditable={!!isEditable}
        wrapper={Component}
        typographyVariant={props.variant}
        {...props}
      />
    );
  };

  return (
    <Box
      id={id}
      className="textbox-component"
      sx={{
        maxWidth: '1100px',
        width: '100%',
        margin: '0 auto',
        gap: textBoxGap,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {texts?.map((text, index) => {
        switch (text.type) {
          case 'title':
            return renderEditableText(text, index, Typography, {
              variant: 'h2',
            });
          case 'h1':
            return renderEditableText(text, index, Typography, {
              variant: 'h1',
              component: 'h1',
            });
          case 'h2':
            return renderEditableText(text, index, Typography, {
              variant: 'h2',
              component: 'h2',
            });
          case 'h3':
            return renderEditableText(text, index, Typography, {
              variant: 'h3',
            });
          case 'h4':
            return renderEditableText(text, index, Typography, {
              variant: 'h4',
            });
          case 'h5':
            return renderEditableText(text, index, Typography, {
              variant: 'h5',
            });
          case 'h6':
            return renderEditableText(text, index, Typography, {
              variant: 'h6',
            });
          case 'subtitle':
            return renderEditableText(text, index, Typography, {
              variant: 'h4',
            });
          case 'paragraph':
            return renderEditableText(text, index, Typography, {
              variant: 'body1',
            });
          case 'body1':
            return renderEditableText(text, index, Typography, {
              variant: 'body1',
            });
          case 'body2':
            return renderEditableText(text, index, Typography, {
              variant: 'body2',
            });
          case 'primaryButton':
            return (
              <Box
                key={index}
                sx={{ width: '100%', textAlign: (sx as any)?.textAlign }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => handleButtonClick(text.link)}
                  sx={{ width: 'fit-content' }}
                >
                  <EditableMarkdownText
                    content={text.contents}
                    onSave={(newContent) => handleTextChange(index, newContent)}
                    isEditable={!!isEditable}
                    wrapper="span"
                  />
                </Button>
              </Box>
            );
          case 'button':
            return (
              <Box
                key={index}
                sx={{ width: '100%', textAlign: (sx as any)?.textAlign }}
              >
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => handleButtonClick(text.link)}
                  sx={{ width: 'fit-content' }}
                >
                  <EditableMarkdownText
                    content={text.contents}
                    onSave={(newContent) => handleTextChange(index, newContent)}
                    isEditable={!!isEditable}
                    wrapper="span"
                  />
                </Button>
              </Box>
            );
          case 'secondaryButton':
            return (
              <Box
                key={index}
                sx={{ width: '100%', textAlign: (sx as any)?.textAlign }}
              >
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => handleButtonClick(text.link)}
                  sx={{ width: 'fit-content' }}
                >
                  <EditableMarkdownText
                    content={text.contents}
                    onSave={(newContent) => handleTextChange(index, newContent)}
                    isEditable={!!isEditable}
                    wrapper="span"
                  />
                </Button>
              </Box>
            );
          case 'textButton':
            return (
              <Box
                key={index}
                sx={{ width: '100%', textAlign: (sx as any)?.textAlign }}
              >
                <Button
                  variant="text"
                  onClick={() => handleButtonClick(text.link)}
                  sx={{
                    width: 'fit-content',
                  }}
                >
                  <EditableMarkdownText
                    content={text.contents}
                    onSave={(newContent) => handleTextChange(index, newContent)}
                    isEditable={!!isEditable}
                    wrapper="span"
                  />
                </Button>
              </Box>
            );
          case 'divider':
            return (
              <Box
                key={index}
                sx={{
                  width: '100%',
                  height: '1px',
                  bgcolor: 'divider',
                  my: 2,
                }}
              />
            );
          default:
            return null;
        }
      })}
    </Box>
  );
};

export default TextBox;
